import { useState, useEffect } from 'react';
import axios from 'axios';
import { Sidebar } from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { 
  Loader2, 
  Calendar, 
  MapPin, 
  TrendingUp, 
  TrendingDown,
  DollarSign,
  Users,
  Target,
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Minus,
  Info
} from 'lucide-react';
import { formatCurrency, cn } from '../lib/utils';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// KPI Row Component - shows actual vs target (+ stretch) with variance
// Supports provisional state for PAX-dependent metrics
const KPIRow = ({ 
  label, 
  actual, 
  target, 
  stretch = null,  // Non-punitive stretch goal
  unit = '', 
  isPercent = false, 
  isCurrency = false, 
  inverse = false,
  provisional = false  // Gray out if PAX missing
}) => {
  const variance = target > 0 ? ((actual - target) / target * 100) : 0;
  const isGood = inverse ? variance <= 0 : variance >= 0;
  const isNeutral = Math.abs(variance) < 5;
  
  const formatValue = (val) => {
    if (isCurrency) return formatCurrency(val);
    if (isPercent) return `${val.toFixed(1)}%`;
    return `${val.toFixed(2)}${unit}`;
  };

  // Provisional state (PAX-dependent metrics without PAX)
  if (provisional) {
    return (
      <div className="grid grid-cols-12 gap-2 py-3 px-4 border-b border-slate-100 bg-slate-50/50 items-center opacity-60">
        <div className="col-span-4 font-medium text-slate-500 text-sm">{label}</div>
        <div className="col-span-2 text-right font-mono text-slate-400">—</div>
        <div className="col-span-2 text-right font-mono text-slate-400">{formatValue(target)}</div>
        <div className="col-span-4 text-right">
          <Badge variant="outline" className="text-xs text-amber-600 border-amber-300 bg-amber-50">
            Provisional – Finalizes when PAX posts
          </Badge>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-12 gap-2 py-3 px-4 border-b border-slate-100 hover:bg-slate-50 transition-colors items-center">
      <div className="col-span-4 font-medium text-slate-700 text-sm">
        {label}
        {stretch && (
          <span className="text-xs text-slate-400 ml-1">(Stretch: {isPercent ? `${stretch}%` : stretch})</span>
        )}
      </div>
      <div className="col-span-2 text-right font-mono text-slate-900">{formatValue(actual)}</div>
      <div className="col-span-2 text-right font-mono text-slate-500">{formatValue(target)}</div>
      <div className="col-span-2 text-right font-mono">
        <span className={cn(
          "inline-flex items-center gap-1",
          isNeutral ? "text-slate-500" : isGood ? "text-emerald-600" : "text-red-500"
        )}>
          {isNeutral ? (
            <Minus className="w-3 h-3" />
          ) : isGood ? (
            <TrendingUp className="w-3 h-3" />
          ) : (
            <TrendingDown className="w-3 h-3" />
          )}
          {variance >= 0 ? '+' : ''}{variance.toFixed(1)}%
        </span>
      </div>
      <div className="col-span-2 flex justify-end">
        {isGood || isNeutral ? (
          <CheckCircle2 className="w-5 h-5 text-emerald-500" />
        ) : (
          <AlertTriangle className="w-5 h-5 text-amber-500" />
        )}
      </div>
    </div>
  );
};

// Score Weight Tooltip Component
const WeightTooltip = ({ weights }) => {
  if (!weights) return null;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button className="ml-2 p-1 rounded-full hover:bg-white/20 transition-colors">
            <Info className="w-5 h-5 text-white/80" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="bg-slate-800 text-white p-3 max-w-xs">
          <p className="font-semibold mb-2">Score Weights</p>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span>Budget & Labor</span>
              <span className="font-mono">{weights.budget_labor}%</span>
            </div>
            <div className="flex justify-between">
              <span>Conversion & Engagement</span>
              <span className="font-mono">{weights.conversion}%</span>
            </div>
            <div className="flex justify-between">
              <span>Revenue Quality</span>
              <span className="font-mono">{weights.revenue_quality}%</span>
            </div>
            <div className="flex justify-between">
              <span>Product Mix</span>
              <span className="font-mono">{weights.product_mix}%</span>
            </div>
            <div className="flex justify-between">
              <span>Capture Effectiveness</span>
              <span className="font-mono">{weights.capture}%</span>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default function DailyScorecardPage() {
  const { user, isManager, isExecutive } = useAuth();
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [scorecard, setScorecard] = useState(null);
  const [entry, setEntry] = useState(null);
  const [targets, setTargets] = useState(null);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    if (selectedLocation && selectedDate) {
      fetchScorecard();
    }
  }, [selectedLocation, selectedDate]);

  const fetchLocations = async () => {
    try {
      const res = await axios.get(`${API}/locations`);
      setLocations(res.data);
      
      // For managers, auto-assign their location (no dropdown)
      if (isManager && user?.location_id) {
        setSelectedLocation(user.location_id);
      } else if (res.data.length > 0) {
        setSelectedLocation(res.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch locations:', error);
      toast.error('Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  const fetchScorecard = async () => {
    setFetching(true);
    try {
      // Fetch entries for location and date
      const entriesRes = await axios.get(`${API}/entries`, {
        params: { location_id: selectedLocation, start_date: selectedDate, end_date: selectedDate }
      });
      
      if (entriesRes.data.length === 0) {
        setEntry(null);
        setScorecard(null);
        setTargets(null);
        setFetching(false);
        return;
      }

      const entryData = entriesRes.data[0];
      setEntry(entryData);

      // Fetch scorecard for this entry
      const scorecardRes = await axios.get(`${API}/scorecards/${entryData.id}`);
      setScorecard(scorecardRes.data);

      // Fetch targets for this location
      const targetsRes = await axios.get(`${API}/targets/${selectedLocation}`);
      setTargets(targetsRes.data);

    } catch (error) {
      console.error('Failed to fetch scorecard:', error);
      setEntry(null);
      setScorecard(null);
    } finally {
      setFetching(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
        </div>
      </div>
    );
  }

  const selectedLocationData = locations.find(l => l.id === selectedLocation);
  const paxIsFinal = entry?.pax && entry.pax > 0;

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-violet-50 via-white to-fuchsia-50">
      <Sidebar />
      
      <main className="flex-1 p-4 lg:p-8" data-testid="daily-scorecard-page">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 font-['Outfit']">
            Daily Scorecard
          </h1>
          <p className="text-slate-600 mt-1">
            {isManager ? 'View your location\'s detailed KPIs' : 'View detailed KPIs for any location and date'}
          </p>
        </div>

        {/* Location & Date Selector */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Location Dropdown - Always visible */}
              <div className="flex-1">
                <Label className="text-sm text-slate-600 mb-2 block">Select Location</Label>
                <select
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  className="w-full h-12 px-4 rounded-lg border border-slate-300 bg-white text-slate-900 font-medium focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                  data-testid="location-dropdown"
                >
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>
                      {loc.name} ({loc.code}) - {loc.region}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Date Picker */}
              <div className="w-full md:w-48">
                <Label className="text-sm text-slate-600 mb-2 block">Select Date</Label>
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="h-12"
                  data-testid="date-picker"
                />
              </div>

              {/* Refresh Button */}
              <div className="flex items-end">
                <Button 
                  onClick={fetchScorecard} 
                  disabled={fetching}
                  className="h-12 bg-violet-600 hover:bg-violet-700"
                  data-testid="refresh-btn"
                >
                  {fetching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Load Scorecard'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Loading State */}
        {fetching && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
          </div>
        )}

        {/* No Entry State */}
        {!fetching && !entry && (
          <Card>
            <CardContent className="py-16 text-center">
              <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">No Entry Found</h3>
              <p className="text-slate-500">
                No data has been submitted for {selectedLocationData?.name} on {selectedDate}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Scorecard Display */}
        {!fetching && entry && scorecard && (
          <div className="space-y-6">
            {/* Header Card with Overall Score */}
            <Card className="overflow-hidden">
              <div className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white p-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div>
                    <Badge 
                      className={paxIsFinal
                        ? 'bg-emerald-500 hover:bg-emerald-600 mb-2' 
                        : 'bg-amber-500 hover:bg-amber-600 mb-2'
                      }
                    >
                      {paxIsFinal ? 'Final – PAX Adjusted' : 'Preliminary – PAX Pending'}
                    </Badge>
                    <h2 className="text-2xl font-bold font-['Outfit']">{selectedLocationData?.name}</h2>
                    <p className="text-white/80 mt-1">
                      {new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                    </p>
                    <p className="text-white/60 text-sm mt-1">Manager: {entry.manager_name}</p>
                  </div>
                  
                  <div className="text-center">
                    <div className="flex items-center justify-center">
                      <div className={cn(
                        "w-24 h-24 rounded-2xl flex items-center justify-center font-mono font-bold text-3xl text-white shadow-lg",
                        scorecard.overall_score >= 85 ? 'bg-emerald-500' :
                        scorecard.overall_score >= 70 ? 'bg-lime-500' :
                        scorecard.overall_score >= 55 ? 'bg-amber-500' : 'bg-red-500'
                      )}>
                        {Math.round(scorecard.overall_score)}
                      </div>
                      {/* Weight Tooltip */}
                      <WeightTooltip weights={scorecard.weights} />
                    </div>
                    <p className="text-white/80 text-sm mt-2">Overall Score</p>
                  </div>
                </div>
              </div>
            </Card>

            {/* KPI Tables */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              
              {/* Budget & Sales KPIs */}
              <Card data-testid="budget-kpis">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-emerald-600" />
                    Budget & Sales Performance
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {/* Header Row */}
                  <div className="grid grid-cols-12 gap-2 py-2 px-4 bg-slate-100 text-xs font-semibold text-slate-600 uppercase tracking-wide">
                    <div className="col-span-4">KPI</div>
                    <div className="col-span-2 text-right">Actual</div>
                    <div className="col-span-2 text-right">Target</div>
                    <div className="col-span-2 text-right">Variance</div>
                    <div className="col-span-2 text-right">Status</div>
                  </div>
                  
                  <KPIRow 
                    label="Daily Sales Budget" 
                    actual={entry.actual_total_sales} 
                    target={entry.daily_sales_budget} 
                    isCurrency 
                  />
                  <KPIRow 
                    label="Budget Attainment" 
                    actual={(entry.actual_total_sales / entry.daily_sales_budget) * 100} 
                    target={targets?.budget_attainment_target || 100}
                    stretch={targets?.budget_attainment_stretch || 105}
                    isPercent 
                  />
                  <KPIRow 
                    label="Revenue/Labor Hour" 
                    actual={entry.actual_total_sales / entry.labor_hours_worked} 
                    target={targets?.revenue_per_labor_hour_target || 50} 
                    isCurrency 
                  />
                  <KPIRow 
                    label="Trans/Labor Hour" 
                    actual={entry.transactions / entry.labor_hours_worked} 
                    target={targets?.transactions_per_labor_hour_target || 3} 
                    unit="" 
                  />
                  <KPIRow 
                    label="Avg Transaction Value" 
                    actual={entry.actual_total_sales / (entry.transactions || 1)} 
                    target={targets?.atv_target || 25} 
                    isCurrency 
                  />
                </CardContent>
              </Card>

              {/* Capture & Conversion KPIs */}
              <Card data-testid="capture-kpis">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="w-5 h-5 text-violet-600" />
                    Capture & Conversion
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="grid grid-cols-12 gap-2 py-2 px-4 bg-slate-100 text-xs font-semibold text-slate-600 uppercase tracking-wide">
                    <div className="col-span-4">KPI</div>
                    <div className="col-span-2 text-right">Actual</div>
                    <div className="col-span-2 text-right">Target</div>
                    <div className="col-span-2 text-right">Variance</div>
                    <div className="col-span-2 text-right">Status</div>
                  </div>
                  
                  {/* PAX-dependent metrics - show as provisional if no PAX */}
                  <KPIRow 
                    label="Capture Rate" 
                    actual={paxIsFinal ? (entry.people_captured / entry.pax) * 100 : 0} 
                    target={targets?.capture_rate_target || 80} 
                    isPercent 
                    provisional={!paxIsFinal}
                  />
                  <KPIRow 
                    label="Overall Sales %" 
                    actual={paxIsFinal ? (entry.transactions / entry.pax) * 100 : 0} 
                    target={targets?.overall_sales_target || 10} 
                    isPercent 
                    provisional={!paxIsFinal}
                  />
                  <KPIRow 
                    label="Per Cap Sales" 
                    actual={paxIsFinal ? entry.actual_total_sales / entry.pax : 0} 
                    target={targets?.per_cap_sales_target || 5} 
                    isCurrency 
                    provisional={!paxIsFinal}
                  />
                  
                  <KPIRow 
                    label="Capture → Seen Flow" 
                    actual={(entry.seen / entry.people_captured) * 100} 
                    target={targets?.seen_rate_target || 50}
                    stretch={targets?.seen_rate_best_practice || 65}
                    isPercent 
                  />
                  <KPIRow 
                    label="Seen → Sale Conv" 
                    actual={(entry.transactions / entry.seen) * 100} 
                    target={15} 
                    isPercent 
                  />
                </CardContent>
              </Card>

              {/* Product Mix KPIs */}
              <Card data-testid="product-mix-kpis">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Target className="w-5 h-5 text-blue-600" />
                    Product Mix Analysis
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="grid grid-cols-12 gap-2 py-2 px-4 bg-slate-100 text-xs font-semibold text-slate-600 uppercase tracking-wide">
                    <div className="col-span-4">KPI</div>
                    <div className="col-span-2 text-right">Actual</div>
                    <div className="col-span-2 text-right">Target</div>
                    <div className="col-span-2 text-right">Variance</div>
                    <div className="col-span-2 text-right">Status</div>
                  </div>
                  
                  <KPIRow 
                    label="Core Photo Sales" 
                    actual={entry.core_photo_sales || 0} 
                    target={entry.actual_total_sales * 0.75} 
                    isCurrency 
                  />
                  <KPIRow 
                    label="Supplemental Sales" 
                    actual={entry.supplemental_sales || 0} 
                    target={entry.actual_total_sales * ((targets?.supplemental_sales_pct_target || 25) / 100)} 
                    isCurrency 
                  />
                  <KPIRow 
                    label="Supplemental %" 
                    actual={((entry.supplemental_sales || 0) / entry.actual_total_sales) * 100} 
                    target={targets?.supplemental_sales_pct_target || 25}
                    stretch={targets?.supplemental_sales_pct_stretch || 30}
                    isPercent 
                  />
                  {(entry.hard_product_sales != null || entry.digital_product_sales != null) && (
                    <>
                      <KPIRow 
                        label="Digital Mix %" 
                        actual={((entry.digital_product_sales || 0) / ((entry.hard_product_sales || 0) + (entry.digital_product_sales || 0) || 1)) * 100} 
                        target={targets?.digital_mix_target || 60} 
                        isPercent 
                      />
                      <KPIRow 
                        label="Supp $/Labor Hour" 
                        actual={(entry.supplemental_sales || 0) / entry.labor_hours_worked} 
                        target={(targets?.revenue_per_labor_hour_target || 50) * ((targets?.supplemental_sales_pct_target || 25) / 100)} 
                        isCurrency 
                      />
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Raw Data Summary */}
              <Card data-testid="raw-data">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-slate-600" />
                    Raw Input Data
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="p-3 rounded-lg bg-slate-50">
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Budget</p>
                      <p className="font-mono font-semibold text-lg">{formatCurrency(entry.daily_sales_budget)}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-slate-50">
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Actual Sales</p>
                      <p className="font-mono font-semibold text-lg">{formatCurrency(entry.actual_total_sales)}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-slate-50">
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Labor Hours</p>
                      <p className="font-mono font-semibold text-lg">{entry.labor_hours_worked}h</p>
                    </div>
                    <div className="p-3 rounded-lg bg-slate-50">
                      <p className="text-xs text-slate-500 uppercase tracking-wide">PAX</p>
                      <p className={cn("font-mono font-semibold text-lg", !paxIsFinal && "text-amber-600")}>
                        {paxIsFinal ? entry.pax.toLocaleString() : 'Pending'}
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-slate-50">
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Captured</p>
                      <p className="font-mono font-semibold text-lg">{entry.people_captured.toLocaleString()}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-slate-50">
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Seen</p>
                      <p className="font-mono font-semibold text-lg">{entry.seen.toLocaleString()}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-slate-50">
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Transactions</p>
                      <p className="font-mono font-semibold text-lg">{entry.transactions.toLocaleString()}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-slate-50">
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Core Photo</p>
                      <p className="font-mono font-semibold text-lg">{formatCurrency(entry.core_photo_sales || 0)}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-slate-50">
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Supplemental</p>
                      <p className="font-mono font-semibold text-lg">{formatCurrency(entry.supplemental_sales || 0)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Diagnostic Insights */}
            {scorecard.alerts && scorecard.alerts.length > 0 && (
              <Card data-testid="insights-card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                    Diagnostic Insights
                  </CardTitle>
                  <CardDescription>Auto-generated · Non-editable</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {scorecard.alerts.map((alert, index) => (
                      <div 
                        key={index}
                        className={cn(
                          "flex items-start gap-3 p-3 rounded-lg",
                          alert.includes('Excellent') || alert.includes('Strong') || alert.includes('Solid') || alert.includes('on target') || alert.includes('stretch goal')
                            ? "bg-emerald-50 text-emerald-800"
                            : "bg-amber-50 text-amber-800"
                        )}
                      >
                        <ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span className="text-sm">{alert}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
